import { useState } from "react";
import { Link } from "react-router-dom";
import { Bookmark, Share2, Eye } from "lucide-react";
import { toast } from "sonner";
import { categoryLabel } from "../lib/categories";
import Reveal from "./Reveal";

const KEY = "mm-bookmarks";
const getSaved = () => JSON.parse(localStorage.getItem(KEY) || "[]");

export default function ArticleCard({ article, index = 0, large = false }) {
  const [saved, setSaved] = useState(() => getSaved().includes(article.slug));

  const toggleSave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const list = getSaved();
    const next = saved ? list.filter((s) => s !== article.slug) : [...list, article.slug];
    localStorage.setItem(KEY, JSON.stringify(next));
    setSaved(!saved);
    toast.success(saved ? "Removed from your reading list" : "Saved to your reading list");
  };

  const share = (e) => {
    e.preventDefault();
    e.stopPropagation();
    navigator.clipboard.writeText(`${window.location.origin}/article/${article.slug}`);
    toast.success("Link copied — pass it on");
  };

  return (
    <Reveal delay={(index % 6) * 0.06} className={large ? "lg:col-span-2" : ""}>
      <Link to={`/article/${article.slug}`} data-testid={`article-card-item-${article.slug}`}
        className="group block relative border border-white/10 bg-surface overflow-hidden h-full">
        <div className={`overflow-hidden relative ${large ? "aspect-[16/8]" : "aspect-[16/10]"}`}>
          <img src={article.image_url} alt={article.title} loading="lazy"
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 ease-out" />
          <div className="absolute inset-0 bg-gradient-to-t from-obsidian/70 via-transparent to-transparent" />
          <div className="absolute top-3 right-3 flex gap-2 opacity-100 lg:opacity-0 lg:group-hover:opacity-100 transition-opacity duration-300">
            <button data-testid="article-bookmark-button" onClick={toggleSave} title="Save"
              className={`w-9 h-9 rounded-full backdrop-blur-md flex items-center justify-center transition-colors duration-300 ${saved ? "bg-kinetic text-obsidian" : "bg-obsidian/60 text-porcelain hover:text-kinetic"}`}>
              <Bookmark size={15} fill={saved ? "currentColor" : "none"} />
            </button>
            <button data-testid="article-share-button" onClick={share} title="Share"
              className="w-9 h-9 rounded-full bg-obsidian/60 backdrop-blur-md flex items-center justify-center text-porcelain hover:text-kinetic transition-colors duration-300">
              <Share2 size={15} />
            </button>
          </div>
        </div>
        <div className="p-5 lg:p-6">
          <div className="flex items-center gap-3 mb-3">
            <span className="text-[10px] font-mono uppercase tracking-[0.2em] text-kinetic">{categoryLabel(article.category)}</span>
            {article.kind === "trivia" && (
              <span data-testid="article-kind-badge" className="text-[10px] font-mono uppercase tracking-[0.2em] text-crimson border border-crimson/40 px-2 py-0.5">Quiz</span>
            )}
            {article.kind === "review" && (
              <span data-testid="article-kind-badge" className="text-[10px] font-mono uppercase tracking-[0.2em] text-cobalt border border-cobalt/40 px-2 py-0.5">Review</span>
            )}
            {article.kind === "spotlight" && (
              <span data-testid="article-kind-badge" className="text-[10px] font-mono uppercase tracking-[0.2em] text-kinetic border border-kinetic/40 px-2 py-0.5">Spotlight</span>
            )}
            <span className="w-1 h-1 rounded-full bg-white/20" />
            <span className="text-[10px] font-mono uppercase tracking-[0.2em] text-ash">{article.read_minutes} min</span>
          </div>
          <h3 className={`font-display font-bold leading-snug group-hover:text-kinetic transition-colors duration-300 ${large ? "text-2xl lg:text-3xl" : "text-xl"}`}>
            {article.title}
          </h3>
          <p className="text-ash text-sm leading-relaxed mt-3 line-clamp-2">{article.excerpt}</p>
          <div className="flex items-center justify-between mt-5 pt-4 border-t border-white/5">
            <span className="text-[11px] font-mono text-ash">{article.author_name}</span>
            <span className="flex items-center gap-1.5 text-[11px] font-mono text-ash">
              <Eye size={12} /> {article.views || 0}
            </span>
          </div>
        </div>
      </Link>
    </Reveal>
  );
}
